import Select, {
  CoercedMenuPlacement,
  ControlProps,
  MenuPlacement,
  MenuPosition,
  components,
} from 'react-select';
import { ChevronDown } from 'lucide-react';
import styles from './styles.module.css';
import { SelectOption, SelectOptionsWithCategories } from '@/types/types';
import classNames from 'classnames';
import { IconType, Icon } from '@/ui/';

interface SelectProps {
  id?: string;
  disabled?: boolean;
  error?: boolean;
  options: SelectOption[] | SelectOptionsWithCategories[];
  field?: unknown;
  iconTextPosition?: 'start' | 'end' | 'center';
  placeholder?: string;
  inputId?: string;
  defValue?: SelectOption;
  isMulti?: boolean;
  variant?: 'rowInput' | 'full';
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  icon?: boolean;
  selectSize?: 'small' | 'medium' | 'large' | 'custom';
  isSearchable?: boolean;
  showDropdownIndicator?: boolean;
  portal?: boolean;
  onChange?: (value: any, action?: any) => void;
  label?: string;
  labelClassName?: string;
  menuPosition?: MenuPosition;
  coercedMenuPlacement?: CoercedMenuPlacement;
  menuPlacement?: MenuPlacement;
  testId?: string;
  className?: string;
}

const SingleValue = ({ children, ...props }: any) => {
  const option = props.selectProps?.value;
  const showIcon = option?.iconType || props.selectProps?.icon;
  const pos = props.selectProps?.iconTextPosition || 'start';

  return (
    <components.SingleValue {...props}>
      <div className={`${styles.iconText} ${styles[pos]}`}>
        {showIcon && (
          <Icon
            type={option?.iconType || IconType.Finances}
            width={24}
            height={24}
            fill='transparent'
          />
        )}
        <span
          className={`text-sm ${props.isDisabled ? 'text-muted-foreground' : 'text-foreground'}`}
        >
          {children}
        </span>
      </div>
    </components.SingleValue>
  );
};

const OptionContent = ({ children, ...props }: any) => {
  const option = props.data;
  const showIcon = option?.iconType;
  const pos = props.selectProps?.iconTextPosition || 'start';

  return (
    <components.Option {...props}>
      <div className={`${styles.iconText} ${styles[pos]}`}>
        {showIcon && option?.iconType && (
          <Icon type={option.iconType} width={20} height={20} fill='transparent' />
        )}
        <span className='text-sm'>{children}</span>
      </div>
    </components.Option>
  );
};

const DropdownIndicator = (props: any) => {
  if (!props.selectProps?.showDropdownIndicator || props.isDisabled) {
    return null;
  }
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            props.selectProps?.menuIsOpen ? 'rotate-180' : ''
          }`}
        />
      </components.DropdownIndicator>
    )
  );
};

export function CustomSelect({
  id = '',
  disabled,
  error,
  options,
  field,
  inputId,
  iconTextPosition = 'start',
  selectSize = 'small',
  variant,
  onChange,
  isMulti = false,
  placeholder = 'Select...',
  isSearchable = false,
  showDropdownIndicator = true,
  icon = false,
  portal = false,
  defValue,
  label,
  labelClassName = 'text-sm font-light',
  menuPosition = 'fixed',
  menuPlacement = 'auto',
  testId,
  className,
}: SelectProps) {
  const control = classNames(styles.control, styles[selectSize], {
    [styles.rowInput]: variant === 'rowInput',
    [styles.full]: variant === 'full',
  });
  const container = classNames(styles.container, styles[selectSize]);
  const indicatorsContainer = classNames(styles.indicatorsContainer, styles[selectSize]);
  const selectContainer = classNames(
    styles.selectContainer,
    {
      [styles.rowInput]: variant === 'rowInput',
    },
    className,
  );

  const injectedSelectProps = {
    showDropdownIndicator,
    icon,
    iconTextPosition,
  };

  const customStyles = {
    control: (baseStyles: any, state: ControlProps<any, any>) => ({
      ...baseStyles,
      backgroundColor: state.isDisabled ? 'hsl(var(--muted))' : 'hsl(var(--background))',
      borderColor: error ? 'hsl(var(--destructive))' : 'hsl(var(--input))',
      minHeight: selectSize === 'large' ? '44px' : selectSize === 'custom' ? '100%' : '36px',
      maxHeight: selectSize === 'large' ? '44px' : selectSize === 'custom' ? '100%' : '36px',
      borderRadius: 'calc(var(--radius) - 2px)',
      boxShadow: 'none',
      color: 'hsl(var(--foreground))',
      fontSize: '14px',
      fontWeight: '400',
      '&:hover': {
        borderColor: error ? 'hsl(var(--destructive))' : 'hsl(var(--ring))',
      },
    }),
    input: (baseStyles: any) => ({
      ...baseStyles,
      fontSize: '14px',
      fontWeight: '400',
      color: 'hsl(var(--foreground))',
    }),
    singleValue: (baseStyles: any) => ({
      ...baseStyles,
      color: 'hsl(var(--foreground))',
    }),
    placeholder: (baseStyles: any) => ({
      ...baseStyles,
      color: 'hsl(var(--muted-foreground))',
    }),
    option: (
      baseStyles: any,
      { isSelected, isFocused }: { isSelected?: boolean; isFocused?: boolean },
    ) => ({
      ...baseStyles,
      backgroundColor: isSelected
        ? 'hsl(var(--accent))'
        : isFocused
          ? 'hsl(var(--accent))'
          : 'hsl(var(--popover))',
      color:
        isSelected || isFocused
          ? 'hsl(var(--accent-foreground))'
          : 'hsl(var(--popover-foreground))',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      fontSize: '14px',
      fontWeight: '400',
      cursor: 'default',
      borderRadius: 'calc(var(--radius) - 4px)',
    }),
    menuPortal: (baseStyles: any) => ({
      ...baseStyles,
      zIndex: 9999,
      position: 'fixed',
    }),
    menu: (baseStyles: any) => ({
      ...baseStyles,
      borderRadius: 'calc(var(--radius) - 2px)',
      border: '1px solid hsl(var(--border))',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      backgroundColor: 'hsl(var(--popover))',
      zIndex: 10,
      maxWidth: variant === 'full' ? '100%' : '325px',
      overflow: 'hidden',
    }),
    menuList: (baseStyles: any) => ({
      ...baseStyles,
      padding: '4px',
      maxHeight: '200px',
      maxWidth: '100%',
      borderRadius: 'calc(var(--radius) - 2px)',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
  };

  return (
    <div className={selectContainer} {...(testId ? { 'data-testid': testId } : {})}>
      {label && (
        <label className={styles.label}>
          <span className={labelClassName}>{label}</span>
        </label>
      )}
      <Select
        id={id}
        classNames={{
          container: () => container,
          control: () => control,
          indicatorsContainer: () => indicatorsContainer,
          indicatorSeparator: () => '',
          dropdownIndicator: () => styles.dropdownIndicator,
          menu: () => styles.menu,
          menuList: () => styles.menuList,
          input: () => styles.input,
          option: () => styles.option,
        }}
        menuPortalTarget={portal ? document.body : undefined}
        options={options}
        isDisabled={disabled}
        inputId={inputId}
        placeholder={placeholder}
        components={{
          SingleValue: (props: any) => (
            <SingleValue
              {...props}
              selectProps={{ ...props.selectProps, ...injectedSelectProps }}
            />
          ),
          DropdownIndicator: (props: any) => (
            <DropdownIndicator
              {...props}
              selectProps={{ ...props.selectProps, ...injectedSelectProps }}
            />
          ),
          Option: (props: any) => (
            <OptionContent
              {...props}
              selectProps={{ ...props.selectProps, ...injectedSelectProps }}
            />
          ),
        }}
        styles={customStyles}
        isSearchable={isSearchable}
        onChange={onChange}
        defaultValue={defValue}
        isMulti={isMulti}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        {...(field as object)}
      />
    </div>
  );
}
